import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AdminRole,
  CreateAdminPayload,
  SurgoAdmin,
  createAdmin,
  deleteAdmin,
  getAdminRoles,
  getAdmins,
  updateAdminRole,
} from "../../../api/surgo";
import { formatRoleTitle, getSessionAdmin, SUPER_USER_ROLE } from "../../../utils/adminSession";
import { useConfirmDialog } from "../../shared/ConfirmDialogProvider";
import { useToast } from "../../shared/Toast";
import SearchBar from "../../shared/SearchBar";
import SortableTableCell from "../../shared/SortableTableCell";
import { useSortable } from "../../shared/useSortable";
import { addButtonSx, deleteButtonSx, editButtonSx } from "../../shared/buttonStyles";
import PageHeader from "../../shared/PageHeader";
import PageCard from "../../shared/PageCard";
import AdminModal from "./AdminModal";
import TempPasswordDialog from "./TempPasswordDialog";

const DEFAULT_ROLE = "internal_admin";

/** The API answers `{dataPath, message}`, or a list of them for validation errors. */
const apiErrorMessage = (err: any, fallback: string): string => {
  const data = err?.response?.data;
  const message = Array.isArray(data) ? data[0]?.message : data?.message;
  return message || fallback;
};

const AdminUsersComponent: React.FC = () => {
  const { confirm } = useConfirmDialog();
  const { showSuccess, showError } = useToast();
  const currentAdminId = getSessionAdmin()?.id;

  const [admins, setAdmins] = useState<SurgoAdmin[]>([]);
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState<{ open: boolean; admin?: SurgoAdmin }>({ open: false });
  const [createdAdmin, setCreatedAdmin] = useState<{ name: string; password: string } | null>(null);

  const fetchAdmins = useCallback(async () => {
    try {
      setIsLoadingData(true);
      const [adminsData, rolesData] = await Promise.all([getAdmins(), getAdminRoles()]);
      setAdmins(adminsData);
      setRoles(rolesData);
    } catch (err) {
      showError(apiErrorMessage(err, "Error fetching admins"));
    } finally {
      setIsLoadingData(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const filteredAdmins = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return admins;
    return admins.filter((admin) =>
      [admin.first_name, admin.last_name, admin.email, formatRoleTitle(admin.role)]
        .join(" ")
        .toLowerCase()
        .includes(term)
    );
  }, [admins, search]);

  const { sortedData: sortedAdmins, sortConfig, requestSort } = useSortable(filteredAdmins, "first_name");

  const handleCloseModal = () => setModal({ open: false });

  const handleSubmit = async (payload: CreateAdminPayload) => {
    const editing = modal.admin;
    try {
      setIsSubmitting(true);
      if (editing) {
        await updateAdminRole(editing.id, payload.role_id);
        showSuccess(`${editing.first_name} ${editing.last_name}'s role was updated`);
      } else {
        const password = await createAdmin(payload);
        setCreatedAdmin({ name: `${payload.first_name} ${payload.last_name}`, password });
        showSuccess("Admin created successfully");
      }
      handleCloseModal();
      fetchAdmins();
    } catch (err) {
      showError(apiErrorMessage(err, editing ? "Error updating admin" : "Error creating admin"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (admin: SurgoAdmin) => {
    const confirmed = await confirm({
      title: "Delete admin",
      message: `Are you sure you want to delete ${admin.first_name} ${admin.last_name}? They will lose access to the admin portal.`,
      confirmText: "Delete",
    });
    if (!confirmed) return;

    try {
      await deleteAdmin(admin.id);
      setAdmins((prev) => prev.filter((item) => item.id !== admin.id));
      showSuccess("Admin deleted successfully");
    } catch (err) {
      showError(apiErrorMessage(err, "Error deleting admin"));
    }
  };

  const defaultRoleId = roles.find((role) => role.title === DEFAULT_ROLE)?.id;

  const renderTable = () => (
    <>
      <SearchBar value={search} onChange={setSearch} placeholder="Search admins..." />
      <TableContainer sx={{ flex: 1, overflow: "auto" }}>
        <Table aria-label="admin users table" stickyHeader>
          <TableHead>
            <TableRow>
              <SortableTableCell label="Name" sortKey="first_name" active={sortConfig?.key === "first_name"} direction={sortConfig?.direction ?? "asc"} onClick={requestSort} />
              <SortableTableCell label="Email" sortKey="email" active={sortConfig?.key === "email"} direction={sortConfig?.direction ?? "asc"} onClick={requestSort} align="center" />
              <SortableTableCell label="Role" sortKey="role" active={sortConfig?.key === "role"} direction={sortConfig?.direction ?? "asc"} onClick={requestSort} align="center" />
              <TableCell align="center" sx={{ backgroundColor: "#FAFAFA", fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedAdmins.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4, color: "#878787" }}>
                  No admins found
                </TableCell>
              </TableRow>
            )}
            {sortedAdmins.map((admin) => {
              const isCurrentAdmin = admin.id === currentAdminId;
              return (
                <TableRow key={admin.id} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                  <TableCell component="th" scope="row">
                    {admin.first_name} {admin.last_name}
                    {isCurrentAdmin && (
                      <Chip label="You" size="small" sx={{ ml: 1, height: 20, fontSize: "11px" }} />
                    )}
                  </TableCell>
                  <TableCell align="center">{admin.email}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={formatRoleTitle(admin.role)}
                      size="small"
                      sx={
                        admin.role === SUPER_USER_ROLE
                          ? { backgroundColor: "#FFF3E0", color: "#E65100", fontWeight: 500 }
                          : { backgroundColor: "#E3F2FD", color: "#1565C0", fontWeight: 500 }
                      }
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      variant="contained"
                      onClick={() => setModal({ open: true, admin })}
                      disabled={isCurrentAdmin}
                      sx={editButtonSx}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => handleDelete(admin)}
                      disabled={isCurrentAdmin}
                      sx={deleteButtonSx}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <PageHeader title="Admin users" subtitle="Manage who can access the admin portal" count={admins.length}>
        <Button variant="contained" onClick={() => setModal({ open: true })} sx={addButtonSx}>
          Add admin
        </Button>
      </PageHeader>

      {modal.open && (
        <AdminModal
          open={modal.open}
          admin={modal.admin}
          roles={roles}
          defaultRoleId={defaultRoleId}
          isSubmitting={isSubmitting}
          onClose={handleCloseModal}
          onSubmit={handleSubmit}
        />
      )}

      {createdAdmin && (
        <TempPasswordDialog
          open
          adminName={createdAdmin.name}
          password={createdAdmin.password}
          onClose={() => setCreatedAdmin(null)}
        />
      )}

      <PageCard>
        {isLoadingData ? (
          <Box sx={{ textAlign: "center", py: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          renderTable()
        )}
      </PageCard>
    </Box>
  );
};

export default AdminUsersComponent;
